import { useState, useEffect } from 'react';
import axios from 'axios';

import { softError, ErrorWithFields } from '../../log';
import { LoadingState, NonEmptyArray, Term } from '../../types';
import { exponentialBackoff, isAxiosNetworkError } from '../../utils/misc';
import Cancellable from '../../utils/cancellable';
import { CRAWLER_BASE_URL } from '../../constants';

const CRAWLER_INDEX_URL = `${CRAWLER_BASE_URL}/index.json`;

/**
 * Downloads the list of terms that the crawler has valid data for.
 * Repeatedly attempts to load in the case of errors,
 * and cancels itself once the parent context is unmounted.
 */
export default function useDownloadTerms(): LoadingState<NonEmptyArray<Term>> {
  const [state, setState] = useState<LoadingState<NonEmptyArray<Term>>>({
    type: 'loading',
  });

  // Fetch all terms via the crawler's self-hosted index
  useEffect(() => {
    const loadOperation = new Cancellable();
    async function load(): Promise<void> {
      let attemptNumber = 1;
      while (!loadOperation.isCancelled) {
        try {
          const promise = axios.get<unknown>(CRAWLER_INDEX_URL, {
            timeout: 15000,
          });
          const result = await loadOperation.perform(promise);
          if (result.cancelled) {
            return;
          }

          const payload = result.value.data;
          if (
            typeof payload !== 'object' ||
            payload === null ||
            !('terms' in payload) ||
            !Array.isArray((payload as { terms: unknown }).terms)
          ) {
            throw new Error(
              'The course-data service returned an invalid list of terms.'
            );
          }
          const rawTerms = (payload as { terms: unknown[] }).terms;
          const validTerms = rawTerms.filter(
            (entry): entry is Term =>
              typeof entry === 'object' &&
              entry !== null &&
              typeof (entry as Term).term === 'string' &&
              /^\d{6}$/.test((entry as Term).term) &&
              typeof (entry as Term).finalized === 'boolean'
          );
          if (
            validTerms.length === 0 ||
            validTerms.length !== rawTerms.length
          ) {
            throw new Error(
              'The course-data service returned an invalid list of terms.'
            );
          }
          const newTerms = [...validTerms].sort((a, b) =>
            b.term.localeCompare(a.term)
          );

          setState({
            type: 'loaded',
            result: newTerms as NonEmptyArray<Term>,
          });

          return;
        } catch (err) {
          // Report the error to Sentry if not a network error
          if (!isAxiosNetworkError(err)) {
            softError(
              new ErrorWithFields({
                message: 'error fetching list of terms',
                source: err,
                fields: {
                  url: CRAWLER_INDEX_URL,
                },
              })
            );
          }

          // Flag that an error has occurred
          setState({
            type: 'error',
            error:
              err instanceof Error
                ? err
                : new ErrorWithFields({
                    message: 'an error occurred while fetching terms',
                    source: err,
                  }),
            stillLoading: true,
            overview: String(err),
          });
        }

        // Sleep for an exponential backoff between each retry
        const pause = await loadOperation.perform(
          exponentialBackoff(attemptNumber, {
            maxDelay: 5000,
          })
        );
        if (pause.cancelled) return;
        attemptNumber += 1;
      }
    }

    load().catch(() => {
      // Unreachable
    });

    // Cancel the background load when this cleans up
    return (): void => {
      loadOperation.cancel();
    };
  }, [setState]);

  return state;
}
